import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from "@angular/cdk/overlay";
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    OnInit,
    output,
    signal,
    TrackByFunction,
    viewChild
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatIconButton } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { PageEvent } from "@angular/material/paginator";
import { MatRadioButton, MatRadioGroup } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatTableModule } from "@angular/material/table";
import {
    BadgeComponent,
    BadgeVariant,
    ButtonComponent,
    ButtonVariant,
    CheckboxComponent,
    InputComponent,
    InputOutline,
    PaginatorComponent,
    TextareaComponent
} from "@sxm-content-creators/tools-ds";
import { DatePickerComponent } from "../../../components/date-picker/date-picker.component";
import { SnackbarService } from "../../../components/snackbar/snackbar.service";
import { InteractiveDisplayState, InteractiveType } from "../../models/interactive.model";
import {
    InteractiveFormGroup,
    InteractiveFormSubmit,
    InteractiveFormTargetState,
    InteractiveFormValue,
    PollOptionFormGroup,
    PollOptionFormValue
} from "../../models/interactive-form.model";
import {
    ERROR_MESSAGES,
    FORM_CONSTRAINTS,
    PollOptionStatus,
    atLeastOneMediaTypeValidator,
    computePollOptionStatuses,
    datesValidator,
    maxSelectionsAllowedValidator,
    mediaOverrideRequiredValidator,
    pollOptionStatusValidator,
    pollOptionsCountValidator
} from "./interactive-form.validators";
import { debounceTime, map, share, tap } from "rxjs/operators";
import { TenantsApiService } from "../../../shared/services/tenants.api.service";
import { parseSingleColumnCsv } from "../../../shared/csv/single-column-csv";
import {
    EditPollOptionModalComponent,
    EditPollOptionModalData
} from "../edit-poll-option-modal/edit-poll-option-modal.component";

// Fields that are not required for Save Draft but must be filled before the user can publish.
// Their error messages are shown when touched+empty.
const PUBLISH_REQUIRED_FIELDS = ["prompt"] as const satisfies readonly (keyof InteractiveFormValue)[];

function defaultPollOptions(count: number = FORM_CONSTRAINTS.pollOptions.min): PollOptionFormValue[] {
    return Array.from({ length: count }, () => ({ id: null, text: "" }));
}

@Component({
    selector: "app-interactive-form",
    templateUrl: "./interactive-form.component.html",
    styleUrl: "./interactive-form.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ReactiveFormsModule,
        InputOutline,
        InputComponent,
        TextareaComponent,
        CheckboxComponent,
        CdkOverlayOrigin,
        CdkConnectedOverlay,
        ButtonComponent,
        MatFormFieldModule,
        MatInputModule,
        MatRadioGroup,
        MatRadioButton,
        MatSelectModule,
        MatTableModule,
        MatIconButton,
        PaginatorComponent,
        BadgeComponent,
        DatePickerComponent
    ]
})
export class InteractiveFormComponent implements OnInit {
    private readonly formBuilder = inject(FormBuilder);
    private readonly tenantsService = inject(TenantsApiService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly dialog = inject(MatDialog);

    readonly availableTargetStates = input<readonly InteractiveFormTargetState[]>(["DRAFT", "PUBLISHED"]);
    readonly initialValue = input<InteractiveFormValue | null>(null);
    readonly currentState = input<InteractiveDisplayState | null>(null);
    readonly isSubmitting = input(false);
    readonly formSubmit = output<InteractiveFormSubmit>();

    readonly form: InteractiveFormGroup = this.formBuilder.group(
        {
            type: this.formBuilder.nonNullable.control<InteractiveType>("freeform", Validators.required),
            name: this.formBuilder.nonNullable.control("", [
                Validators.required,
                Validators.maxLength(FORM_CONSTRAINTS.name.maxLength)
            ]),
            tenantId: this.formBuilder.nonNullable.control("", Validators.required),
            prompt: ["", Validators.maxLength(FORM_CONSTRAINTS.prompt.maxLength)],
            description: ["", Validators.maxLength(FORM_CONSTRAINTS.description.maxLength)],
            hasStartDate: this.formBuilder.nonNullable.control(false),
            hasEndDate: this.formBuilder.nonNullable.control(false),
            startDate: this.formBuilder.control<string | null>(null),
            endDate: this.formBuilder.control<string | null>(null),
            allowsText: [false],
            allowsAudio: [true],
            allowsVideo: [false],
            maxCharacterOverride: this.formBuilder.control<number | null>(null, [
                Validators.min(FORM_CONSTRAINTS.maxCharacterOverride.min),
                Validators.max(FORM_CONSTRAINTS.maxCharacterOverride.max)
            ]),
            maxDurationOverride: this.formBuilder.control<number | null>(20, [
                Validators.min(FORM_CONSTRAINTS.maxDurationOverride.min),
                Validators.max(FORM_CONSTRAINTS.maxDurationOverride.max)
            ]),
            maxVideoDurationOverride: this.formBuilder.control<number | null>(null, [
                Validators.min(FORM_CONSTRAINTS.maxVideoDurationOverride.min),
                Validators.max(FORM_CONSTRAINTS.maxVideoDurationOverride.max)
            ]),
            isMultiSelect: this.formBuilder.nonNullable.control(false),
            displayResultsOverridable: this.formBuilder.nonNullable.control(true),
            maxSelectionsAllowed: this.formBuilder.control<number | null>(null, [
                Validators.min(FORM_CONSTRAINTS.maxSelectionsAllowed.min),
                Validators.max(FORM_CONSTRAINTS.maxSelectionsAllowed.max)
            ]),
            pollOptions: this.formBuilder.array<PollOptionFormGroup>([])
        },
        {
            validators: [
                atLeastOneMediaTypeValidator,
                mediaOverrideRequiredValidator,
                datesValidator,
                pollOptionsCountValidator,
                maxSelectionsAllowedValidator,
                pollOptionStatusValidator
            ]
        }
    );

    private readonly formEvents = toSignal(this.form.events, { initialValue: null });

    readonly isLiveOrPaused = computed(() => {
        return this.currentState() === "LIVE" || this.currentState() === "PAUSED";
    });

    // Poll options can only be added/edited/removed before an interactive has ever been live
    readonly isPollOptionsEditable = computed(() => {
        const state = this.currentState();
        if (!state) return true;
        return state === "DRAFT" || state === "SCHEDULED";
    });

    readonly isSaveDraftDisabled = computed(() => {
        this.formEvents();
        return this.form.invalid || this.isSubmitting();
    });
    // Non-draft submits require the form to be valid AND publish-required fields to be filled.
    // Those fields don't have Validators.required (so Save Draft can pass), so check them explicitly.
    readonly isSubmitDisabled = computed(() => {
        this.formEvents();
        return (
            this.form.invalid ||
            this.isSubmitting() ||
            PUBLISH_REQUIRED_FIELDS.some(name => !this.form.controls[name].value)
        );
    });

    private readonly showErrors = signal(false);

    private readonly pollOptionsCsvInput = viewChild<ElementRef<HTMLInputElement>>("pollOptionsCsvInput");

    readonly pollOptionsSearchControl = new FormControl("");
    private readonly pollOptionsSearchQuery$ = this.pollOptionsSearchControl.valueChanges.pipe(
        debounceTime(200),
        map(value => (value ?? "").trim().toLowerCase()),
        share()
    );
    private readonly pollOptionsSearchQuery = toSignal(this.pollOptionsSearchQuery$, { initialValue: "" });

    readonly pollOptionsPageIndex = signal(0);
    readonly pollOptionsPageSize = signal(20);

    readonly activePollOptionRows = computed<PollOptionFormValue[]>(() => {
        this.formEvents();
        return this.form.controls.pollOptions.getRawValue();
    });

    readonly pollOptionStatuses = computed(() => computePollOptionStatuses(this.activePollOptionRows()));
    readonly pollOptionStatusCounts = computed(() => {
        const counts: Record<PollOptionStatus, number> = { OK: 0, EMPTY: 0, LENGTHY: 0, DUPLICATE: 0 };
        for (const status of this.pollOptionStatuses()) counts[status]++;
        return counts;
    });
    readonly pollOptionsStatusFilter = signal<PollOptionStatus | "ALL">("ALL");
    readonly pollOptionStatusIssueMessage = computed(() => {
        const counts = this.pollOptionStatusCounts();
        const issueCount = counts.EMPTY + counts.LENGTHY + counts.DUPLICATE;
        if (issueCount === 0) return null;
        const suffix = issueCount === 1 ? "" : "s";
        return `Fix ${issueCount} selection issue${suffix} to continue.`;
    });

    readonly pollOptionCountIssueMessage = computed(() => {
        this.formEvents();
        const count = this.activePollOptionRows().length;
        const min = FORM_CONSTRAINTS.pollOptions.min;
        const max = this.form.controls.isMultiSelect.value
            ? FORM_CONSTRAINTS.pollOptions.maxMultiSelect
            : FORM_CONSTRAINTS.pollOptions.maxSingleSelect;
        if (count < min) {
            return `Add at least ${min} selections to continue.`;
        }
        if (count > max) {
            const excess = count - max;
            return `Remove ${excess} selection${excess === 1 ? "" : "s"} to continue.`;
        }
        return null;
    });

    readonly filteredPollOptionRows = computed(() => {
        const query = this.pollOptionsSearchQuery();
        const statusFilter = this.pollOptionsStatusFilter();
        const rows = this.activePollOptionRows();
        const statuses = this.pollOptionStatuses();
        return rows.filter((row, index) => {
            if (query && !row.text.toLowerCase().includes(query)) return false;
            return !(statusFilter !== "ALL" && statuses[index] !== statusFilter);
        });
    });

    protected readonly clampedPollOptionsPageIndex = computed(() => {
        const rowCount = this.filteredPollOptionRows().length;
        const pageSize = this.pollOptionsPageSize();
        const maxPageIndex = Math.max(0, Math.ceil(rowCount / pageSize) - 1);
        return Math.min(this.pollOptionsPageIndex(), maxPageIndex);
    });

    readonly pagedPollOptionRows = computed(() => {
        const rows = this.filteredPollOptionRows();
        const pageSize = this.pollOptionsPageSize();
        const start = this.clampedPollOptionsPageIndex() * pageSize;
        return rows.slice(start, start + pageSize);
    });

    private syncPollOptionsPageIndex(): void {
        const clamped = this.clampedPollOptionsPageIndex();
        if (this.pollOptionsPageIndex() !== clamped) {
            this.pollOptionsPageIndex.set(clamped);
        }
    }

    readonly isAddPollOptionDisabled = computed(() => {
        this.formEvents();
        const max = this.form.controls.isMultiSelect.value
            ? FORM_CONSTRAINTS.pollOptions.maxMultiSelect
            : FORM_CONSTRAINTS.pollOptions.maxSingleSelect;
        return this.activePollOptionRows().length >= max;
    });

    readonly isRemovePollOptionDisabled = computed(() => {
        return this.activePollOptionRows().length <= 1;
    });

    constructor() {
        const {
            allowsAudio,
            allowsText,
            allowsVideo,
            maxDurationOverride,
            maxCharacterOverride,
            maxVideoDurationOverride
        } = this.form.controls;
        const { hasStartDate, hasEndDate, startDate, endDate } = this.form.controls;
        const { type, isMultiSelect, displayResultsOverridable, maxSelectionsAllowed } = this.form.controls;

        this.pollOptionsSearchQuery$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.pollOptionsPageIndex.set(0);
        });

        allowsAudio.valueChanges.pipe(takeUntilDestroyed()).subscribe(enabled => {
            // when allowsAudio is toggled true, automatically set a max duration override, otherwise clear the value
            maxDurationOverride.setValue(enabled ? 20 : null);
        });
        allowsText.valueChanges.pipe(takeUntilDestroyed()).subscribe(enabled => {
            // when allowsText is toggled true, automatically set a max character override, otherwise clear the value
            maxCharacterOverride.setValue(enabled ? 280 : null);
        });
        allowsVideo.valueChanges.pipe(takeUntilDestroyed()).subscribe(enabled => {
            // when allowsVideo is toggled true, automatically set a max duration override, otherwise clear the value
            maxVideoDurationOverride.setValue(enabled ? 20 : null);
        });
        hasStartDate.valueChanges.pipe(takeUntilDestroyed()).subscribe(enabled => {
            if (!enabled) startDate.setValue(null);
        });
        hasEndDate.valueChanges.pipe(takeUntilDestroyed()).subscribe(enabled => {
            if (!enabled) endDate.setValue(null);
        });
        isMultiSelect.valueChanges.pipe(takeUntilDestroyed()).subscribe(multiselect => {
            if (multiselect) {
                displayResultsOverridable.setValue(false);
                maxSelectionsAllowed.setValue(FORM_CONSTRAINTS.maxSelectionsAllowed.min);
            } else {
                displayResultsOverridable.setValue(true);
                maxSelectionsAllowed.setValue(null);
                // Single-select polls can't be open-ended.
                if (type.value === "poll") hasEndDate.setValue(true);
            }
            this.pollOptionsSearchControl.reset("");
            this.pollOptionsStatusFilter.set("ALL");
        });
        type.valueChanges.pipe(takeUntilDestroyed()).subscribe(newType => {
            allowsText.reset(false);
            allowsAudio.reset(true);
            allowsVideo.reset(false);
            isMultiSelect.reset();
            displayResultsOverridable.reset();
            maxSelectionsAllowed.reset();
            this.setPollOptions(newType === "poll" ? defaultPollOptions() : []);
            this.pollOptionsSearchControl.reset("");
            this.pollOptionsStatusFilter.set("ALL");
            this.syncPollOptionsPageIndex();
        });
    }

    ngOnInit(): void {
        const value = this.initialValue();
        if (!value) return;
        // Populate form with existing data without triggering valueChanges, which
        // would fire the parent's output and treat a load as a user edit.
        this.form.patchValue(value, { emitEvent: false });
        // FormArray.patchValue only fills already-existing rows — it won't create rows to match
        // the loaded data, so pollOptions needs to be rebuilt explicitly.
        this.setPollOptions(value.pollOptions);

        const { tenantId, type, startDate, hasStartDate, isMultiSelect, pollOptions, maxSelectionsAllowed } =
            this.form.controls;

        if (this.currentState() !== null) {
            // these are creation-only fields
            tenantId.disable({ emitEvent: false });
            type.disable({ emitEvent: false });

            this.showErrors.set(true);
        }

        if (this.isLiveOrPaused()) {
            // Disable changes to these fields on a live/paused interactive
            hasStartDate.disable({ emitEvent: false });
            startDate.disable({ emitEvent: false });

            // Disable poll-related inputs
            isMultiSelect.disable({ emitEvent: false });
            pollOptions.disable({ emitEvent: false });
            maxSelectionsAllowed.disable({ emitEvent: false });
        }
    }

    isSingleSelectPoll(): boolean {
        return this.form.controls.type.value === "poll" && !this.form.controls.isMultiSelect.value;
    }

    readonly interactiveTypeOptions: { label: string; value: InteractiveType }[] = [
        { label: "Voice/Text/Video", value: "freeform" },
        { label: "Poll", value: "poll" }
    ];

    readonly tenantOptions = toSignal(
        this.tenantsService.getAuthorizedTenants().pipe(
            tap(items => {
                if (items.length === 1 && !this.form.controls.tenantId.value) {
                    this.form.controls.tenantId.setValue(items[0].id);
                }
            })
        ),
        { initialValue: [] }
    );

    readonly durationOptions: { label: string; value: number }[] = [
        { label: "10 seconds", value: 10 },
        { label: "20 seconds (recommended)", value: 20 },
        { label: "30 seconds", value: 30 },
        { label: "40 seconds", value: 40 },
        { label: "50 seconds", value: 50 },
        { label: "60 seconds", value: 60 }
    ];

    readonly videoDurationOptions: { label: string; value: number }[] = [
        { label: "10 seconds", value: 10 },
        { label: "20 seconds (recommended)", value: 20 },
        { label: "30 seconds", value: 30 }
    ];

    readonly pollTypeOptions: { label: string; value: boolean }[] = [
        { label: "Single-select poll", value: false },
        { label: "Multi-select poll", value: true }
    ];

    readonly displayResultsOptions: { label: string; value: boolean }[] = [
        { label: "Yes", value: true },
        { label: "No", value: false }
    ];

    readonly maxSelectionsOptions: { label: string; value: number }[] = [
        { label: "2", value: 2 },
        { label: "3", value: 3 },
        { label: "4", value: 4 },
        { label: "5", value: 5 }
    ];

    get pollOptionRows(): PollOptionFormGroup[] {
        return this.form.controls.pollOptions.controls;
    }

    private createPollOptionRow(option?: PollOptionFormValue): PollOptionFormGroup {
        return this.formBuilder.group({
            id: this.formBuilder.control<string | null>(option?.id ?? null),
            text: this.formBuilder.nonNullable.control(option?.text ?? "", [
                Validators.required,
                Validators.maxLength(FORM_CONSTRAINTS.pollOptions.textMaxLength)
            ])
        });
    }

    private setPollOptions(options: readonly PollOptionFormValue[]): void {
        const pollOptions = this.form.controls.pollOptions;
        pollOptions.clear({ emitEvent: false });
        for (const option of options) {
            pollOptions.push(this.createPollOptionRow(option), { emitEvent: false });
        }
    }

    onEditPollOption(row: PollOptionFormValue): void {
        const index = this.pollOptionIndex(row);
        if (index === -1) return;

        this.dialog
            .open<EditPollOptionModalComponent, EditPollOptionModalData, string>(EditPollOptionModalComponent, {
                data: { index, text: row.text },
                maxWidth: "80vw"
            })
            .afterClosed()
            .subscribe(text => {
                if (text === undefined) return;
                this.form.controls.pollOptions.at(index).controls.text.setValue(text);
                this.syncPollOptionsPageIndex();
            });
    }

    onRemoveAllPollOptions(): void {
        this.applyPollOptions(defaultPollOptions());
        this.syncPollOptionsPageIndex();
    }

    openPollOptionsCsvPicker(): void {
        this.pollOptionsCsvInput()?.nativeElement.click();
    }

    onPollOptionsCsvSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        // Reset so re-selecting the same file still fires a `change` event.
        input.value = "";
        if (!file) return;

        if (!/\.csv$/i.test(file.name)) {
            this.snackbarService.show("Choose a CSV file (.csv).", "error");
            return;
        }

        const maxSizeMb = FORM_CONSTRAINTS.pollOptions.csvMaxSizeMb;
        if (file.size > maxSizeMb * 1024 * 1024) {
            this.snackbarService.show(`This file is too large. CSV files must be ${maxSizeMb} MB or smaller.`, "error");
            return;
        }

        file.text().then(
            text => this.importPollOptionsCsv(text),
            () => this.snackbarService.show("Couldn't read this file. Please try again.", "error")
        );
    }

    // Importing poll options replaces the full list. An invalid file is rejected whole, never partially imported.
    private importPollOptionsCsv(csvText: string): void {
        const parsed = parseSingleColumnCsv(csvText);
        if (parsed.error) {
            this.snackbarService.show("We couldn’t read this file. Check the format and try again.", "error");
            return;
        }

        const texts = parsed.values;
        if (texts.length === 0) {
            this.snackbarService.show("The CSV file has no selections to import.", "error");
            return;
        }

        const max = FORM_CONSTRAINTS.pollOptions.maxMultiSelect;
        if (texts.length > max) {
            this.snackbarService.show(`This file exceeds the ${max} selection limit. Upload a smaller file.`, "error");
            return;
        }

        this.applyPollOptions(texts.map(text => ({ id: null, text })));
        this.pollOptionsPageIndex.set(0);
    }

    // Delegate clicks from the whole table to a single listener each
    onPollOptionsTableClick(event: Event): void {
        const target = event.target as HTMLElement;
        const button = target.closest<HTMLButtonElement>("button[data-action]");
        if (!button) return;

        const row = this.pollOptionsTableRowFor(button);
        if (!row) return;

        if (button.dataset["action"] === "add") {
            this.onAddPollOptionAfter(row);
        } else if (button.dataset["action"] === "remove") {
            this.onRemovePollOption(row);
        } else if (button.dataset["action"] === "edit") {
            this.onEditPollOption(row);
        }
    }

    private pollOptionsTableRowFor(el: HTMLElement): PollOptionFormValue | undefined {
        const rowEl = el.closest<HTMLElement>("tr[data-row-index]");
        const index = rowEl ? Number(rowEl.dataset["rowIndex"]) : Number.NaN;
        return Number.isNaN(index) ? undefined : this.pagedPollOptionRows()[index];
    }

    onAddPollOptionAfter(row: PollOptionFormValue): void {
        const index = this.pollOptionIndex(row);
        if (index === -1) return;

        this.form.controls.pollOptions.insert(index + 1, this.createPollOptionRow());
        this.syncPollOptionsPageIndex();
    }

    onRemovePollOption(row: PollOptionFormValue): void {
        const index = this.pollOptionIndex(row);
        if (index === -1) return;

        this.form.controls.pollOptions.removeAt(index);
        this.syncPollOptionsPageIndex();
    }

    onPollOptionsPageChange(event: PageEvent): void {
        this.pollOptionsPageIndex.set(event.pageIndex);
    }

    onPollOptionsPageSizeChange(pageSize: number): void {
        this.pollOptionsPageSize.set(pageSize);
        this.pollOptionsPageIndex.set(0);
    }

    setPollOptionsStatusFilter(filter: PollOptionStatus | "ALL"): void {
        this.pollOptionsStatusFilter.set(filter);
        this.pollOptionsPageIndex.set(0);
    }

    pollOptionStatus(row: PollOptionFormValue): PollOptionStatus {
        return this.pollOptionStatuses()[this.pollOptionIndex(row)] ?? "OK";
    }

    // The row's position in the full, unfiltered option list. Used for the display index,
    // which should renumber as options are added/removed/filtered.
    pollOptionIndex(row: PollOptionFormValue): number {
        return this.activePollOptionRows().indexOf(row);
    }

    private applyPollOptions(options: readonly PollOptionFormValue[]): void {
        this.setPollOptions(options);
        this.form.controls.pollOptions.updateValueAndValidity();
    }

    isTooltipOverlayOpen = false;
    positions: ConnectedPosition[] = [
        {
            originX: "center",
            originY: "bottom",
            overlayX: "center",
            overlayY: "top",
            offsetY: 4
        },
        {
            originX: "center",
            originY: "top",
            overlayX: "center",
            overlayY: "bottom",
            offsetY: -4
        }
    ];

    showOverlay(): void {
        this.isTooltipOverlayOpen = true;
    }

    hideOverlay(): void {
        this.isTooltipOverlayOpen = false;
    }

    onSaveDraft(): void {
        this.tryEmit("DRAFT");
    }

    onPublish(): void {
        this.tryEmit("PUBLISHED");
    }

    onSavePaused(): void {
        this.tryEmit("PAUSED");
    }

    private tryEmit(targetState: InteractiveFormTargetState): void {
        if (!this.availableTargetStates().includes(targetState)) return;
        const isDisabled = targetState === "DRAFT" ? this.isSaveDraftDisabled() : this.isSubmitDisabled();
        if (isDisabled) return;
        this.formSubmit.emit({ value: this.form.getRawValue() as InteractiveFormValue, targetState });
    }

    fieldError(name: keyof InteractiveFormValue, ...groupErrorKeys: readonly string[]): string | null {
        this.formEvents();
        const ctrl = this.form.controls[name];
        if (!ctrl.touched && !this.showErrors()) return null;
        const messages = ERROR_MESSAGES[name];
        if (!messages) return null;
        // Control-level errors first
        if (ctrl.errors) {
            for (const key of Object.keys(ctrl.errors)) {
                if (messages[key]) return messages[key] ?? null;
            }
        }
        // Surface a "required" message for empty publish-required fields (prompt, description) —
        // they don't have Validators.required, so the control-level path above won't catch them.
        if (!ctrl.value && messages["required"]) {
            return messages["required"] ?? null;
        }
        // Then group-level errors
        const groupErrors = this.form.errors ?? {};
        for (const key of groupErrorKeys) {
            if (groupErrors[key] && messages[key]) return messages[key] ?? null;
        }
        return null;
    }

    protected readonly constraints = FORM_CONSTRAINTS;
    protected readonly ButtonVariant = ButtonVariant;
    protected readonly pollOptionsPageSizeOptions = [20, 50, 100];
    protected readonly pollOptionsTableColumns = ["index", "option", "charCount", "status", "actions"];
    protected readonly pollOptionRowTrackBy: TrackByFunction<PollOptionFormValue> = (_, row) =>
        this.pollOptionIndex(row);
    protected readonly pollOptionsReadOnlyTableColumns = ["index", "option", "charCount"];
    protected readonly pollOptionsActiveTableColumns = computed(() =>
        this.isPollOptionsEditable() ? this.pollOptionsTableColumns : this.pollOptionsReadOnlyTableColumns
    );
    protected readonly pollOptionStatusBadgeLibrary: Record<PollOptionStatus, BadgeVariant> = {
        OK: BadgeVariant.PILL_GREEN,
        LENGTHY: BadgeVariant.PILL_RED,
        EMPTY: BadgeVariant.PILL_RED,
        DUPLICATE: BadgeVariant.PILL_RED
    };
}
